import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Icon,
  Table,
} from '@makeably/creativex-design-system';
import Accordion from 'components/atoms/Accordion';
import SelectableTable, { propTypes as tableProps } from 'components/organisms/SelectableTable';
import AuthenticityTokenInput from 'components/reusable/forms/AuthenticityTokenInput';
import { HiddenInput } from 'components/reusable/HiddenInput';
import ArrayHelper from 'components/utils/ArrayHelper';
import {
  track,
} from 'utilities/mixpanel';
import styles from './AdAccountSelection.module.css';

const propTypes = {
  addMixpanelData: PropTypes.func.isRequired,
  collapsibleTables: PropTypes.arrayOf(
    PropTypes.shape({
      actionUrl: PropTypes.string,
      collapsibleTableHeaders: PropTypes.arrayOf(PropTypes.string),
      rows: PropTypes.shape({
        columns: PropTypes.arrayOf(PropTypes.node),
        key: PropTypes.string,
      }),
      title: PropTypes.string,
    }),
  ).isRequired,
  formData: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFormAction: PropTypes.func.isRequired,
  tables: PropTypes.arrayOf(
    PropTypes.shape({
      businessCenter: PropTypes.shape({
        apiId: PropTypes.string,
        name: PropTypes.string,
      }),
      rows: tableProps.rows,
    }),
  ).isRequired,
};

const renderTable = ({
  actionUrl,
  collapsibleTableHeaders,
  iconColor,
  iconName,
  linkText,
  rows,
  title,
}) => {
  const header = (
    <div className="u-alignCenter u-flexRow u-gap-8">
      <Icon
        color={iconColor}
        name={iconName}
      />
      <div>{ `${title} (${rows.length})` }</div>
      <div className="t-caption-1">
        { 'Visit the ' }
        <a
          href={actionUrl}
          rel="noreferrer"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          { linkText }
        </a>
        { ' for more info.' }
      </div>
    </div>
  );

  const tableHeaders = collapsibleTableHeaders.map((tableHeader) => ({ value: tableHeader }));
  const tableRows = rows.map(({ columns, key }) => ({
    cells: columns.map((value) => ({ value })),
    key,
  }));
  return (
    <div className={styles.table}>
      <Card>
        <Accordion
          divider={false}
          header={header}
          initiallyExpanded={false}
        >
          <Table
            headers={tableHeaders}
            rows={tableRows}
          />
        </Accordion>
      </Card>
    </div>
  );
};

class AdAccountSelection extends React.Component {
  constructor(props) {
    super(props);
    track('view_ad_account_selection');

    this.state = {
      allSelected: [],
      selected: [],
    };
  }

  onDataChange() {
    const { addMixpanelData, onFormAction } = this.props;
    const { selected } = this.state;
    const numAdAccounts = selected.length;
    onFormAction(numAdAccounts > 0);
    addMixpanelData({ totalAdAccounts: numAdAccounts });
  }

  onAllSelect(businessCenterApiId) {
    this.setState((prevState) => {
      const allSelected = ArrayHelper.withOrWithoutValue(
        prevState.allSelected,
        businessCenterApiId,
      );

      const tableValues = this.tableDataForBizCenter(businessCenterApiId);

      let updatedValues = prevState.selected;

      if (allSelected.includes(businessCenterApiId)) {
        // Add table rows to list of selected
        tableValues.forEach((row) => {
          const isSelected = ArrayHelper.exactObjectInArray(updatedValues, row);
          // Add if not there already
          if (!isSelected) {
            updatedValues.push(row);
          }
        });
      } else {
        // Remove table rows from list of selected
        tableValues.forEach((row) => {
          updatedValues = updatedValues.filter((elm) => (
            // Filter out ones that match
            !Object.keys(elm).every((key) => elm[key] === row[key])
          ));
        });
      }

      return {
        allSelected,
        selected: updatedValues,
      };
    }, () => this.onDataChange());
  }

  onRowSelect(businessCenterApiId, data) {
    this.setState((prevState) => {
      const updatedValues = ArrayHelper.withOrWithoutExactObjectFromArray(prevState.selected, data);

      // Check if all business center api ids are now selected
      const tableValues = this.tableDataForBizCenter(businessCenterApiId);
      const valuesForBizCenter = updatedValues.filter((elm) => (
        elm.businessCenterApiId === businessCenterApiId
      ));

      let { allSelected } = prevState;

      if (tableValues.length === valuesForBizCenter.length) {
        allSelected.push(businessCenterApiId);
      } else if (allSelected.includes(businessCenterApiId)) {
        allSelected = ArrayHelper.removeFromArray(allSelected, businessCenterApiId);
      }

      return {
        allSelected,
        selected: updatedValues,
      };
    }, () => this.onDataChange());
  }

  tableDataForBizCenter(businessCenterApiId) {
    const { tables } = this.props;
    const { rows } = tables.find(({ businessCenter }) => (
      businessCenter.apiId === businessCenterApiId
    ));
    return rows.filter(({ disabledSelected }) => !disabledSelected).map(({ data }) => data);
  }

  rowSelected(apiId) {
    const { selected } = this.state;
    return selected.filter((data) => data.businessCenterApiId === apiId);
  }

  render() {
    const {
      headers,
      tables,
      collapsibleTables,
      formData: {
        id,
        url,
      },
    } = this.props;

    const { selected: stateSelected } = this.state;

    return (
      <>
        {
          tables.map(({ businessCenter, rows }) => (
            <SelectableTable
              key={businessCenter.apiId}
              className={styles.selectableTable}
              headers={headers}
              rows={rows}
              selected={this.rowSelected(businessCenter.apiId)}
              title={businessCenter.name}
              selectAll
              onAllSelect={() => this.onAllSelect(businessCenter.apiId)}
              onRowSelect={(rowData) => this.onRowSelect(businessCenter.apiId, rowData)}
            />
          ))
        }
        { collapsibleTables.map(renderTable) }
        <form action={url} id={id} method="post">
          <AuthenticityTokenInput />
          {
            stateSelected.map(({
              businessCenterApiId, name, value,
            }) => (
              <React.Fragment key={`${businessCenterApiId}_${value}`}>
                <HiddenInput
                  name="business_centers[][api_id]"
                  value={businessCenterApiId}
                />
                <HiddenInput
                  name="business_centers[]ad_accounts[][api_id]"
                  value={value}
                />
                <HiddenInput
                  name="business_centers[]ad_accounts[][name]"
                  value={name}
                />
              </React.Fragment>
            ))
          }
        </form>
      </>
    );
  }
}

AdAccountSelection.propTypes = propTypes;

export default AdAccountSelection;
